// User-facing times always render in the tenant's timezone, never the device's.
// The API sends UTC ISO on the wire; the backend tenant tz is Asia/Tashkent
// (mirrors design/runtime WORK_HOURS.tz). Formatting timestamps with the device
// locale (or worse, slicing HH:MM out of the raw UTC string) showed the wrong
// wall-clock — e.g. a 13:01 Tashkent call rendered as 08:01.

export const TENANT_TZ = 'Asia/Tashkent'

const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']

interface TzParts {
  y: string
  mo: string
  d: string
  h: string
  mi: string
}

function tzParts(d: Date): TzParts {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TENANT_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const g = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return { y: g('year'), mo: g('month'), d: g('day'), h: g('hour'), mi: g('minute') }
}

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** "13:01" — HH:MM in the tenant timezone. */
export function fmtTime(iso: string | null | undefined): string {
  const d = toDate(iso)
  if (!d) return ''
  const { h, mi } = tzParts(d)
  return `${h}:${mi}`
}

/** "08.06" — DD.MM in the tenant timezone. */
export function fmtShortDate(iso: string | null | undefined): string {
  const d = toDate(iso)
  if (!d) return ''
  const { d: dd, mo } = tzParts(d)
  return `${dd}.${mo}`
}

/** "08.06, 13:01" — DD.MM, HH:MM in the tenant timezone. */
export function fmtDateTime(iso: string | null | undefined): string {
  const d = toDate(iso)
  if (!d) return ''
  const { d: dd, mo, h, mi } = tzParts(d)
  return `${dd}.${mo}, ${h}:${mi}`
}

/** "8 июн 13:01" — day month-abbr <sep> HH:MM in the tenant timezone. */
export function fmtDayMonthTime(iso: string | null | undefined, sep = ' '): string {
  const d = toDate(iso)
  if (!d) return ''
  const { d: dd, mo, h, mi } = tzParts(d)
  return `${Number(dd)} ${MONTHS_RU[Number(mo) - 1]}${sep}${h}:${mi}`
}

/** Minutes since tenant-tz midnight for the "now" cursor on the day timeline. */
export function nowMinutesTenantTz(): number {
  const { h, mi } = tzParts(new Date())
  return Number(h) * 60 + Number(mi)
}
